import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import SkeletonCard from "@/components/base/SkeletonCard";
import EmptyState from "@/components/base/EmptyState";
import FilterPanel from "@/pages/category/components/FilterPanel";
import SortBar from "@/pages/category/components/SortBar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  collectTags,
  getCategoryBySlug,
  getVideosByCategory,
  isWithinDuration,
  isWithinTime,
  sortVideos,
  type SortKey,
} from "@/lib/video";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const navigate = useNavigate();
  const category = getCategoryBySlug(type);
  const categoryVideos = useMemo(() => getVideosByCategory(type), [type]);
  const tagOptions = useMemo(() => collectTags(categoryVideos), [categoryVideos]);

  const [time, setTime] = useState("all");
  const [duration, setDuration] = useState("all");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: category ? `${category.name}视频 - 逼特逼视频` : "视频分类 - 逼特逼视频",
    description: category
      ? `${category.description} 在逼特逼视频浏览${category.name}分类下的精彩内容，支持多维筛选与排序。`
      : "在逼特逼视频浏览各类型视频内容。",
    keywords: category
      ? `${category.name},逼特逼视频,视频分类,在线视频`
      : "逼特逼视频,视频分类",
  });

  useEffect(() => {
    setTime("all");
    setDuration("all");
    setSelectedTags([]);
    setSort("latest");
    setVisible(PAGE_SIZE);
    setFilterOpen(false);
  }, [type]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [time, duration, selectedTags, sort]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 450);
    return () => clearTimeout(timer);
  }, [type, time, duration, selectedTags, sort]);

  const result = useMemo(() => {
    const base = categoryVideos.filter(
      (v) =>
        isWithinTime(v.uploadDate, time) &&
        isWithinDuration(v.durationSeconds, duration) &&
        (selectedTags.length === 0 || selectedTags.some((tag) => v.tags.includes(tag))),
    );
    return sortVideos(base, sort);
  }, [categoryVideos, time, duration, selectedTags, sort]);

  const activeFilterCount =
    (time !== "all" ? 1 : 0) + (duration !== "all" ? 1 : 0) + selectedTags.length;
  const hasActiveFilter = activeFilterCount > 0;

  const handleClear = () => {
    setTime("all");
    setDuration("all");
    setSelectedTags([]);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的分类可能已被移除，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const visibleList = result.slice(0, visible);

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />

      <div className="mb-6 mt-4">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-xl`} />
          </span>
          {category.name}
        </h1>
        <p className="mt-2 text-sm text-foreground-600 md:text-base">{category.description}</p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-5">
            <FilterPanel
              currentType={type}
              time={time}
              duration={duration}
              tagOptions={tagOptions}
              selectedTags={selectedTags}
              onTypeChange={(slug) => navigate(`/category/${slug}`)}
              onTimeChange={setTime}
              onDurationChange={setDuration}
              onToggleTag={toggleTag}
              onClear={handleClear}
              hasActiveFilter={hasActiveFilter}
            />
          </div>
        </aside>

        <div>
          <SortBar
            total={result.length}
            sort={sort}
            onSortChange={setSort}
            onOpenFilter={() => setFilterOpen(true)}
            activeFilterCount={activeFilterCount}
          />

          {loading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <SkeletonCard count={6} />
            </div>
          ) : result.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="未找到匹配视频"
              description="当前筛选条件下没有符合条件的视频，试试调整或清空筛选条件。"
              actionLabel="清空筛选"
              onAction={handleClear}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visibleList.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              {visible < result.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    加载更多
                    <span className="text-xs text-foreground-400">
                      （剩余 {result.length - visible}）
                    </span>
                    <i className="ri-arrow-down-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setFilterOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-[320px] max-w-[85%] overflow-y-auto bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <span className="text-base font-semibold text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              currentType={type}
              time={time}
              duration={duration}
              tagOptions={tagOptions}
              selectedTags={selectedTags}
              onTypeChange={(slug) => {
                navigate(`/category/${slug}`);
                setFilterOpen(false);
              }}
              onTimeChange={setTime}
              onDurationChange={setDuration}
              onToggleTag={toggleTag}
              onClear={handleClear}
              hasActiveFilter={hasActiveFilter}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              查看结果（{result.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}